/**
 * RubricSelector Component
 *
 * Phase 8.1: 6-point rubric scale selector for assessment questions
 * Scores: 0, 20, 40, 60, 80, 100
 */

import { Check } from 'lucide-react';
import type { RubricOption } from '@guild/shared';

interface RubricSelectorProps {
  value: number | null;
  onChange: (score: number) => void;
  options: RubricOption[];
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function RubricSelector({
  value,
  onChange,
  options,
  disabled = false,
  size = 'md',
}: RubricSelectorProps) {
  // Sort options by score descending (100 -> 0)
  const sortedOptions = [...options].sort((a, b) => b.score - a.score);

  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  return (
    <div className="flex flex-wrap gap-2">
      {sortedOptions.map((option) => {
        const isSelected = value === option.score;

        return (
          <button
            key={option.score}
            type="button"
            onClick={() => onChange(option.score)}
            disabled={disabled}
            title={option.description}
            className={`
              ${sizeClasses[size]}
              relative flex items-center justify-center gap-1.5
              font-semibold rounded-lg border-2 transition-all duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
              ${
                isSelected
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }
            `}
            style={
              isSelected
                ? {
                    backgroundColor: option.colorCode ?? '#00674a',
                    borderColor: option.colorCode ?? '#00674a',
                  }
                : {
                    borderColor: option.colorCode ? `${option.colorCode}40` : undefined,
                  }
            }
          >
            {isSelected && <Check className="w-3.5 h-3.5" />}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * RubricLabel Component
 *
 * Shows the selected rubric label and description
 */
interface RubricLabelProps {
  value: number | null;
  options: RubricOption[];
}

export function RubricLabel({ value, options }: RubricLabelProps) {
  if (value === null) {
    return <span className="text-gray-400 italic">Not scored</span>;
  }

  const option = options.find((o) => o.score === value);
  if (!option) {
    return <span className="text-gray-500">{value}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold text-white"
        style={{ backgroundColor: option.colorCode ?? '#00674a' }}
      >
        {option.score}
      </span>
      <span className="text-sm font-medium text-gray-700">{option.label}</span>
    </div>
  );
}

export default RubricSelector;
